import type { AbstractLevel } from 'abstract-level';
import { Level } from 'level';
import type { IDManagedAgent } from './types/agent.js';
import type { DwnRpcRequest } from './rpc-client.js';
import { Convert } from '../common/index.js';

type LevelDatabase = AbstractLevel<string | Buffer | Uint8Array, string, string>;

/**
 * Serialized entry stored in the outbox for replay. Uses the same shape as
 * the RPC payload so replay is a single sendDwnRequest per endpoint.
 */
export type OutboxEntryPayload = {
  targetDid: string;
  dwnUrls: string[];
  message: Record<string, unknown>;
  dataBase64?: string;
};

export type OutboxOptions = {
  agent?: IDManagedAgent;
  dataPath?: string;
  db?: LevelDatabase;
};

/**
 * Persists outbound DWN send requests that failed due to network and replays
 * them when draining. Entries are stored in FIFO order (by key).
 */
export class Outbox {
  private _agent?: IDManagedAgent;
  private _store: LevelDatabase;

  constructor(options: OutboxOptions = {}) {
    const { agent, dataPath = 'data/AGENT/OUTBOX', db } = options;
    this._agent = agent;
    if (db) {
      this._store = db.sublevel('outbox') as LevelDatabase;
    } else {
      this._store = new Level(dataPath) as LevelDatabase;
    }
  }

  get agent(): IDManagedAgent {
    if (this._agent === undefined) {
      throw new Error('Outbox: Unable to determine agent execution context.');
    }
    return this._agent;
  }

  set agent(agent: IDManagedAgent) {
    this._agent = agent;
  }

  /**
   * Enqueue a failed send for later replay. Payload must be the serializable
   * form (message object and optional data as base64).
   */
  async enqueue(entry: OutboxEntryPayload): Promise<void> {
    const key = `seq-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
    const value = JSON.stringify(entry);
    await this._store.put(key, value);
  }

  /**
   * Try each queued item: for each entry, try each dwnUrl until one succeeds;
   * on success delete from queue; on total failure leave in queue for next drain.
   */
  async drain(): Promise<void> {
    const agent = this.agent;
    const entries: [string, OutboxEntryPayload][] = [];

    for await (const [key, value] of this._store.iterator()) {
      if (!key.startsWith('seq-')) continue;
      try {
        const payload = JSON.parse(value as string) as OutboxEntryPayload;
        entries.push([key, payload]);
      } catch {
        // Skip malformed entries
      }
    }

    for (const [key, payload] of entries) {
      const { targetDid, dwnUrls, message, dataBase64 } = payload;
      let sent = false;

      for (const dwnUrl of dwnUrls) {
        try {
          const request: DwnRpcRequest = {
            dwnUrl,
            targetDid,
            message,
            ...(dataBase64 !== undefined
              ? {
                  data: new Blob([
                    new Uint8Array(Convert.base64Url(dataBase64).toUint8Array()),
                  ]),
                }
              : {}),
          };
          await agent.rpcClient.sendDwnRequest(request);
          sent = true;
          break;
        } catch {
          // Try next endpoint
        }
      }

      if (sent) {
        await this._store.del(key);
      }
    }
  }

  /**
   * Clear all queued entries (for tests or reset).
   */
  async clear(): Promise<void> {
    const keys: string[] = [];
    for await (const [key] of this._store.iterator()) {
      if (key.startsWith('seq-')) keys.push(key);
    }
    await this._store.batch(keys.map((key) => ({ type: 'del' as const, key })) as any);
  }
}

/** @deprecated Use OutboxEntryPayload */
export type OutboxEntry = OutboxEntryPayload;
